import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import { Field, Modal, Notice } from '@/components/Modal';
import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';
import { errorMessage } from '@/lib/format';
import { useSites } from '@/lib/queries';
import { QrActions } from '@/pages/safe/QrActions';

import {
  STATUS_BADGE,
  STATUS_LABEL,
  useVoiceHandle,
  useVoiceLink,
  useVoiceReissue,
  useVoiceReports,
  voiceUrl,
  type VoiceKind,
  type VoiceReport,
  type VoiceStatus,
} from './voiceApi';

const STATUSES: VoiceStatus[] = ['RECEIVED', 'IN_PROGRESS', 'DONE'];

/**
 * 아차사고일지 · 근로자 의견 청취가 같이 쓰는 목록(V576 · DOC-136). 다른 것은 종류(kind)와 제목뿐이다.
 * 줄을 누르면 처리 창(상태 · 조치 내용 · 회신), [QR] 은 현장에 붙이는 QR — 두 메뉴가 같은 QR 하나를 쓴다(근로자가 종류를 고른다).
 */
export function VoiceBoard({ siteId, kind, title }: { siteId: string; kind: VoiceKind; title: string }) {
  const reports = useVoiceReports(siteId, kind);
  const [status, setStatus] = useState<VoiceStatus | ''>('');
  const [editing, setEditing] = useState<VoiceReport | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const rows = (reports.data ?? []).filter((one) => !status || one.status === status);

  return (
    <div className="space-y-5">
      <PageHeader
        title={title}
        actions={
          <>
            <select className="input" style={{ width: 'auto' }} value={status} onChange={(e) => setStatus(e.target.value as VoiceStatus | '')}>
              <option value="">전체</option>
              {STATUSES.map((one) => (
                <option key={one} value={one}>
                  {STATUS_LABEL[one]}
                </option>
              ))}
            </select>
            <button className="btn-secondary" onClick={() => setQrOpen(true)}>
              QR
            </button>
          </>
        }
      />
      {reports.isPending ? (
        <LoadingBlock />
      ) : reports.isError ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <EmptyCard>접수된 것이 없습니다.</EmptyCard>
      ) : (
        <div className="card p-0" style={{ overflowX: 'auto' }}>
          <table className="site-table">
            <thead>
              <tr>
                <th style={{ width: '100px' }}>접수일</th>
                <th style={{ width: '120px' }}>{kind === 'NEAR_MISS' ? '유형' : '분야'}</th>
                <th style={{ width: '140px' }}>위치</th>
                <th style={{ textAlign: 'left' }}>내용</th>
                <th style={{ width: '140px' }}>제보자</th>
                <th style={{ width: '90px' }}>상태</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => setEditing(row)}>
                  <td>{day(row.createdAt)}</td>
                  <td>{row.category}</td>
                  <td>{row.location ?? '—'}</td>
                  <td style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                    {row.content}
                    {(row.photos?.length ?? 0) > 0 && (
                      <span style={{ marginLeft: '6px', fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>📷 {row.photos?.length}</span>
                    )}
                  </td>
                  <td>{reporter(row)}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[row.status] ?? 'badge-gray'}`}>{STATUS_LABEL[row.status] ?? row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <HandleModal siteId={siteId} report={editing} onClose={() => setEditing(null)} />}
      {qrOpen && <VoiceQrModal siteId={siteId} onClose={() => setQrOpen(false)} />}
    </div>
  );
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('sv-SE');
}

function reporter(row: VoiceReport): string {
  const who = [row.affiliation, row.reporterName].filter(Boolean).join(' ');
  return who || '익명';
}

/** 처리 창 — 상태 · 조치 내용 · 회신. 회신은 낸 휴대폰이 접수번호로 읽는다. 버튼은 제목 줄. */
function HandleModal({ siteId, report, onClose }: { siteId: string; report: VoiceReport; onClose: () => void }) {
  const handle = useVoiceHandle(siteId);
  const [status, setStatus] = useState<VoiceStatus>(report.status);
  const [actionNote, setActionNote] = useState(report.actionNote ?? '');
  const [reply, setReply] = useState(report.reply ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    setError(null);
    handle.mutate(
      { id: report.id, body: { status, actionNote, reply } },
      { onSuccess: onClose, onError: (cause) => setError(errorMessage(cause)) },
    );
  };

  return (
    <Modal
      title={`${report.category} · ${day(report.createdAt)}`}
      onClose={onClose}
      closeDisabled={handle.isPending}
      maxWidth="560px"
      actions={
        <button className="btn-primary" disabled={handle.isPending} onClick={save}>
          저장
        </button>
      }
    >
      <div className="space-y-4">
        {error && <Notice tone="warning">{error}</Notice>}
        <table className="site-table">
          <tbody>
            <tr>
              <th style={{ width: '90px' }}>위치</th>
              <td style={{ textAlign: 'left' }}>{report.location ?? '—'}</td>
            </tr>
            {report.occurredOn && (
              <tr>
                <th>발생일</th>
                <td style={{ textAlign: 'left' }}>{report.occurredOn}</td>
              </tr>
            )}
            <tr>
              <th>내용</th>
              <td style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>{report.content}</td>
            </tr>
            {(report.photos?.length ?? 0) > 0 && (
              <tr>
                <th>사진</th>
                <td style={{ textAlign: 'left' }}>
                  <div className="flex flex-wrap gap-2">
                    {(report.photos ?? []).map((src, index) => (
                      <a key={src} href={src} target="_blank" rel="noreferrer">
                        <img
                          src={src}
                          alt={`사진 ${index + 1}`}
                          style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border, #e2e8f0)' }}
                        />
                      </a>
                    ))}
                  </div>
                </td>
              </tr>
            )}
            <tr>
              <th>제보자</th>
              <td style={{ textAlign: 'left' }}>
                {reporter(report)}
                {report.reporterPhone && ` · ${report.reporterPhone}`}
              </td>
            </tr>
            {report.actionByName && (
              <tr>
                <th>처리</th>
                <td style={{ textAlign: 'left' }}>
                  {report.actionByName}
                  {report.actionAt && ` · ${day(report.actionAt)}`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Field label="상태">
          <div className="flex gap-2">
            {STATUSES.map((one) => (
              <button key={one} type="button" className={status === one ? 'btn-primary' : 'btn-secondary'} onClick={() => setStatus(one)}>
                {STATUS_LABEL[one]}
              </button>
            ))}
          </div>
        </Field>
        <Field label="조치 내용">
          <textarea className="input" rows={4} style={{ width: '100%' }} value={actionNote} onChange={(e) => setActionNote(e.target.value)} maxLength={2000} />
        </Field>
        <Field label="회신">
          <textarea className="input" rows={3} style={{ width: '100%' }} value={reply} onChange={(e) => setReply(e.target.value)} maxLength={2000} />
        </Field>
      </div>
    </Modal>
  );
}

/** 현장 QR 창 — 그림 저장 · 주소 복사 · 재발급(앞의 QR 은 안 열린다). 버튼은 제목 줄. */
function VoiceQrModal({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const link = useVoiceLink(siteId, true);
  const reissue = useVoiceReissue(siteId);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const url = link.data ? voiceUrl(link.data.token) : null;

  useEffect(() => {
    if (!url) return;
    let alive = true;
    void QRCode.toDataURL(url, { width: 720, margin: 1, errorCorrectionLevel: 'M' }).then((value) => {
      if (alive) setQr(value);
    });
    return () => {
      alive = false;
    };
  }, [url]);

  const siteName = useSites().data?.find((one) => one.id === siteId)?.name ?? '';

  const doReissue = () => {
    if (!window.confirm('QR 을 새로 만들까요? 지금 붙어 있는 QR 은 더 열리지 않습니다.')) return;
    setError(null);
    reissue.mutate(undefined, { onError: (cause) => setError(errorMessage(cause)) });
  };

  return (
    <Modal
      title="아차사고 · 의견 QR"
      onClose={onClose}
      maxWidth="760px"
      actions={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <QrActions
            url={url}
            qr={qr}
            name={`아차사고_의견_${siteName}`}
            poster={{ title: '아차사고 · 근로자 의견', siteName, lines: ['휴대폰 카메라로 QR 을 비춥니다', '아차사고 또는 의견을 고릅니다', '내용을 적고 제출합니다 (익명 가능)'] }}
          />
          <button className="btn-secondary" disabled={reissue.isPending || !link.data} onClick={doReissue}>
            재발급
          </button>
        </div>
      }
    >
      <div className="space-y-3" style={{ textAlign: 'center' }}>
        {error && <Notice tone="warning">{error}</Notice>}
        <div style={{ width: '320px', height: '320px', margin: '0 auto' }}>
          {qr && <img src={qr} alt="QR" style={{ width: '320px', height: '320px', imageRendering: 'pixelated' }} />}
        </div>
        {url && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
            {url}
          </div>
        )}
      </div>
    </Modal>
  );
}
